/* eslint-disable global-require */
import 'normalize.css';

import { Footer } from '@design-system/footer';
import { PageHeader } from '@design-system/page-header-standard';
import classNames from 'classnames';
import React, { FC, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';

import ExternalLinkDialog from '~/common/ExternalLinkDialog';
import Seo from '~/common/Seo';
import HeaderWrap from '~/components/HeaderWrap';
import HeaderWrapWithProfile from '~/components/HeaderWrapWithProfile';
import TopBar from '~/components/TopBar';
import { ExternalLinkDialogContext } from '~/context/ExternalLinkDialogContext';
import { useContentDirection } from '~/hooks/useContentDirection';
import { useCurrentLang } from '~/hooks/useCurrentLang';
import { useLanguageSwitch } from '~/hooks/useLanguageSwitch';
import { footerMapper } from '~/mappers/footer';
import { infoBarMapper } from '~/mappers/infoBar/infoBarMapper';
import { pageHeaderMapper } from '~/mappers/pageHeader';
import { importAuthComponent, shouldLoadAuth } from '~/utils/importAuth';

import { LayoutProps } from './models';

if (process.env.TOKENS_FEATURE_FLAG === 'true')
  require('~/styles/main.new.scss');
else require('~/styles/main.scss');

const Auth = importAuthComponent(() => import('~/layout/Auth'));

const HeaderComponent = shouldLoadAuth() ? HeaderWrapWithProfile : HeaderWrap;

export const Layout: FC<LayoutProps> = ({
  children,
  className,
  seo,
  headerBasic,
  headerWithSplitter,
  headerSplitterAdditionalData,
  footerBasic,
  footerWithSplitter,
  footerSplitterAdditionalData,
  pageUid,
  locale,
  brand,
  pageHeaderProps,
  infoBar,
  fullPath,
  openGraph,
  mainImagePath,
  profile,
}) => {
  const CSSclassNames = classNames('layout', className);
  const { setCurrentLang } = useContext(ExternalLinkDialogContext);
  const { currentLang } = useCurrentLang();

  useEffect(() => {
    setCurrentLang(currentLang);
  }, [currentLang, setCurrentLang]);

  const languageSwitch = useLanguageSwitch(brand);
  const contentDirection = useContentDirection();

  return (
    <Auth>
      <ExternalLinkDialog />
      <div className={CSSclassNames}>
        <Helmet htmlAttributes={{ dir: contentDirection }} />
        {seo ? (
          <Seo
            {...{
              seo,
              urls: languageSwitch.list,
              pageUid,
              locale,
              fullPath,
              openGraph,
              mainImagePath,
            }}
          />
        ) : null}

        <HeaderComponent
          {...{
            headerBasic,
            headerWithSplitter,
            headerSplitterAdditionalData,
            languageSwitch,
            profile,
          }}
        />

        {infoBar && <TopBar {...infoBarMapper({ ...infoBar })} />}

        <main id="main" className="layout__main-wrapper">
          {!!pageHeaderProps?.pageHeader?.headingBlock?.headline ||
          pageHeaderProps?.pageHeader?.showBreadcrumbs ? (
            <PageHeader {...pageHeaderMapper({ ...pageHeaderProps })} />
          ) : null}
          <div className="layout__main">{children}</div>
        </main>

        {footerWithSplitter || footerBasic ? (
          <Footer
            {...footerMapper({
              footerBasic,
              footerWithSplitter,
              footerSplitterAdditionalData,
            })}
          />
        ) : null}
      </div>
    </Auth>
  );
};
